/**
 * Pure transition → sound-cue mapping.
 *
 * The {@link SoundController} React component owns no logic beyond calling
 * this on each relevant store change. Keeping the decision pure means the
 * "when does each sound fire" contract is unit-tested directly against
 * plain before/after snapshots — no React, no audio backend.
 *
 * Covered events (issue: add sound support):
 *   • queue pop  — a match is found / created (waiting room appears).
 *   • countdown  — one pitched tick per second, then a "go" at zero.
 *   • victory    — match ended, local player's team won.
 *   • defeat     — match ended, local player lost (or draw → defeat sting).
 * Ability-use sound is fired imperatively from the input layer, not here,
 * because it is a local keypress rather than a store transition.
 */

import type { CueId } from "./cues.js";

/** The subset of store state the sound layer reacts to. */
export interface SoundView {
  /** Match lifecycle phase. */
  readonly phase: "waiting" | "countdown" | "active" | "ended";
  /** Winning team index, or null (draw / not ended). */
  readonly winner: number | null;
  /** Local player's seat (== team in 1v1), or null. */
  readonly ownSeat: number | null;
  /** Non-null once we are in a match. Used to detect queue pop. */
  readonly matchId: string | null;
}

/** A cue to fire, plus (for the countdown) the seconds value that drove it. */
export interface PhaseCue {
  readonly cue: CueId;
}

/**
 * Decide which non-countdown cue (if any) a phase/match transition fires.
 *
 * Rules:
 *   • entering a match (matchId null → non-null): queue pop.
 *   • phase → ended: victory if own team won, else defeat (draw = defeat).
 * The countdown ticks are handled separately (they are time-driven within
 * the countdown phase, not a single transition) — see {@link countdownCue}.
 */
export function phaseTransitionCue(prev: SoundView, next: SoundView): PhaseCue | null {
  // Queue pop: a match id appeared where there was none. Covers quickJoin
  // (match found) and explicit create/join alike.
  if (prev.matchId === null && next.matchId !== null) {
    return { cue: "queuePop" };
  }

  // Match ended: win/lose sting. Fire only on the edge into "ended".
  if (prev.phase !== "ended" && next.phase === "ended") {
    const won = next.winner !== null && next.ownSeat !== null && next.winner === next.ownSeat;
    return { cue: won ? "victory" : "defeat" };
  }

  return null;
}

/**
 * Countdown pitch ladder. As the count drops 3 → 2 → 1 the tick rises in
 * pitch; the zero mark is the longer, higher "go". Returns the cue id and
 * the pitch (Hz) for a tick, or the "go" marker at zero.
 *
 * Returns null for values outside the 0..3 window so callers can ignore
 * spurious counts.
 */
export function countdownCue(
  secondsRemaining: number,
): { readonly kind: "tick"; readonly freq: number } | { readonly kind: "go" } | null {
  if (!Number.isInteger(secondsRemaining)) return null;
  if (secondsRemaining <= 0) return { kind: "go" };
  switch (secondsRemaining) {
    case 3:
      return { kind: "tick", freq: 660 };
    case 2:
      return { kind: "tick", freq: 784 };
    case 1:
      return { kind: "tick", freq: 988 };
    default:
      // Longer countdowns (>3s) still tick, at the base pitch.
      return { kind: "tick", freq: 660 };
  }
}
